import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { IInvestmentUtilizationResponse } from "src/models/asset";
import { IGenericResponse } from "src/models/genericModels";
import {
  GoldFD,
  GoldTransaction,
  IGoldHoldingListResponse,
  IGoldListResponse,
  IFDInvestmentListResponse,
  IInvestorDetailResponse,
  IInvestorListResponse,
  ISettingResponse,
  IReportListResponse,
  ISmartSaverInvestmentsListResponse,
  ISmartInvestmentListResponse,
  IDocumentResponse,
  InvestmentSummaryResponse,
  InvestMultippleResponse,
  InvestBody,
  IUploadDocumentResponse,
  InvestmentDetailsResponse,
  StatsResponse,
  UserActionsListResponse,
  ActionSummaryListResponse,
  FDStatusListResponse,
  AsperoKycStatusResponse,
  YubiKycStatusResponse,
  PincodeDetailsResponse,
  BankNameResponse,
  UpdateBankAccountBody,
  FixerraKycStatusResponse,
} from "src/models/investor";
import { UserAuthService } from "./user-auth.service";
import { Group } from "src/constants/global-constants";

@Injectable({
  providedIn: "root",
})
export class InvestorService {
  group: typeof Group = Group;

  constructor(
    private http: HttpClient,
    private userAuthService: UserAuthService
  ) {}

  fetchInvestorList(
    limit: number | null,
    offset: number | null,
    searchTerm: string,
    referringUserId?: number | null
  ): Observable<IInvestorListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const q = `search_query=${encodeURIComponent(
      searchTerm ?? ""
    )}&limit=${limit}&offset=${offset}`;

    // Channel Partner details (staff) must use the staff list API with
    // referring_user_id. Users who are also in CP groups would otherwise hit
    // the CP portal list URL, which has no CP filter — so the param was never sent.
    const filterByReferringCp =
      referringUserId != null &&
      !Number.isNaN(Number(referringUserId)) &&
      Number(referringUserId) > 0;

    let url: string;
    if (filterByReferringCp) {
      url = `${environment.baseUrl}apis/users/staff/v1/list/?${q}&referring_user_id=${referringUserId}`;
    } else if (
      this.userAuthService.hasAnyGroup([
        this.group.INVESTOR_CHANNEL_PARTNER,
        this.group.INVESTOR_MASTER_CHANNEL_PARTNER,
      ])
    ) {
      url = `${environment.baseUrl}apis/users/cp/investor/v1/list/?${q}`;
    } else {
      url = `${environment.baseUrl}apis/users/staff/v1/list/?${q}`;
    }

    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<IInvestorListResponse>(url, httpOptions);
  }

  fetchInvestorDetail(userId: number): Observable<IInvestorDetailResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/details/?user_id=${userId}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<IInvestorDetailResponse>(url, httpOptions);
  }

  updateInvestorDetail(body: any): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/details/`;
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
        "Content-Type": "application/json",
      }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  suggestBonds(
    userId: number,
    assetIds: Array<number>,
    message?: string
  ): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/smart-investment/v1/suggest-bonds/`;
    const body = {
      user_id: userId,
      asset_ids: assetIds,
      message: message || "",
    };
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
        "Content-Type": "application/json",
      }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  fetchInvestorSettings(userId: number): Observable<ISettingResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/setting/v1/?user_id=${userId}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<ISettingResponse>(url, httpOptions);
  }

  fetchInvestmentDetails(
    userId: string
  ): Observable<InvestmentDetailsResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/investment/v1/details/?investment_id=${userId}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<InvestmentDetailsResponse>(url, httpOptions);
  }

  fetchInvestorDocuments(userId: number): Observable<IDocumentResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/documents/?user_id=${userId}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<IDocumentResponse>(url, httpOptions);
  }

  fetchSmartInvestmentList(
    offset: number,
    limit: number,
    status: string,
    startDate: any,
    endDate: any,
    assetClasses: string,
    userid: string
  ): Observable<ISmartInvestmentListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/smart-investment/v1/list/?offset=${offset}&limit=${limit}&status=${status}&start_date=${startDate}&end_date=${endDate}&asset_class_ids=${assetClasses}&user_id=${userid}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<ISmartInvestmentListResponse>(url, httpOptions);
  }

  fetchFDInvestmentList(
    offset: number,
    limit: number,
    status: string,
    startDate: any,
    endDate: any,
    sort_by: string,
    userid: string
  ): Observable<IFDInvestmentListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/fixed-deposits/investments/v1/list/?offset=${offset}&limit=${limit}&status=${status}&start_date=${startDate}&end_date=${endDate}&sort_by=${sort_by}&user_id=${userid}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<IFDInvestmentListResponse>(url, httpOptions);
  }

  fetchSmartSaverInvestmentList(
    offset: number,
    limit: number,
    status: string,
    startDate: any,
    endDate: any,
    sort_by: string,
    userid: string
  ): Observable<ISmartSaverInvestmentsListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/smart-saver/investments/v1/list/?offset=${offset}&limit=${limit}&status=${status}&start_date=${startDate}&end_date=${endDate}&sort_by=${sort_by}&user_id=${userid}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<ISmartSaverInvestmentsListResponse>(url, httpOptions);
  }

  fetchReportList(
    type: string,
    emailReports: boolean,
    startDate: any,
    endDate: any,
    showData: boolean,
    userid: string
  ): Observable<IReportListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/v1/reports/?type=${type}&email_reports=${emailReports}&start_date=${startDate}&end_date=${endDate}&show_data=${showData}&user_id=${userid}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<IReportListResponse>(url, httpOptions);
  }

  verifyDocument(body: any): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/documents/verification/`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  uploadDocuments(body: FormData): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/documents/`;
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
      }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  fetchInvestmentSummary(
    user_id: string
  ): Observable<InvestmentSummaryResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/v1/investment/summary/?user_id=${user_id}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<InvestmentSummaryResponse>(url, httpOptions);
  }

  fetchInvestmentUtilization(
    investmentId: string
  ): Observable<IInvestmentUtilizationResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/fixed-deposits/investment-utilization/v1/list/?investment_id=${investmentId}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<IInvestmentUtilizationResponse>(url, httpOptions);
  }

  fetchUploadDocuments(): Observable<IUploadDocumentResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/v1/document-types/`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<IUploadDocumentResponse>(url, httpOptions);
  }

  investFDAmountInAssetMultiple(
    body: Array<InvestBody>
  ): Observable<InvestMultippleResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/fixed-deposits/investments/v1/multiple/`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.post<InvestMultippleResponse>(url, body, httpOptions);
  }

  approveInvestorKYC(investorId: number): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/crm/staff/v1/verify-kyc/?user_id=${investorId}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.post<IGenericResponse>(url, {}, httpOptions);
  }

  updateDematDetails(body: any): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/demat-account/`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  fetchInvestorStats(
    type: string,
    startDate: any,
    endDate: any
  ): Observable<StatsResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/stats/?type=${type}&start_date=${startDate}&end_date=${endDate}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<StatsResponse>(url, httpOptions);
  }

  fetchSmartSaverStats(
    type: string,
    startDate: any,
    endDate: any
  ): Observable<StatsResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/smart-saver/v1/stats/?type=${type}&start_date=${startDate}&end_date=${endDate}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<StatsResponse>(url, httpOptions);
  }

  fetchUserActionsList(
    offset: number,
    limit: number,
    userid: string
  ): Observable<UserActionsListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/user-actions/?offset=${offset}&limit=${limit}&user_id=${userid}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<UserActionsListResponse>(url, httpOptions);
  }

  fetchActionSummaryList(
    offset: number,
    limit: number
  ): Observable<ActionSummaryListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/user-actions/summary/?offset=${offset}&limit=${limit}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<ActionSummaryListResponse>(url, httpOptions);
  }

  fetchFDStatusList(): Observable<FDStatusListResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/investor/staff/fixed-deposits/v1/statuses/`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<FDStatusListResponse>(url, httpOptions);
  }

  fetchAsperoKYCStatusList(
    user_id: number
  ): Observable<AsperoKycStatusResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/third-party/aspero/v1/kyc-status/?user_id=${user_id}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<AsperoKycStatusResponse>(url, httpOptions);
  }

  fetchYubiKYCStatusList(user_id: number): Observable<YubiKycStatusResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/third-party/yubi/v1/staff/kyc-status/?user_id=${user_id}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<YubiKycStatusResponse>(url, httpOptions);
  }

  fetchFixerraKYCStatus(user_id: number): Observable<FixerraKycStatusResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/third-party/fixerra/v1/kyc-status/?user_id=${user_id}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<FixerraKycStatusResponse>(url, httpOptions);
  }

  fetchPincodeDetails(pinCode: string): Observable<PincodeDetailsResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/address/v1/pincode/details/?pin_code=${pinCode}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<PincodeDetailsResponse>(url, httpOptions);
  }

  updateUserAddress(body: {
    user_id: number;
    location: string;
    landmark: string;
    geo_location_id: number;
  }): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/address/`;
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
        "Content-Type": "application/json",
      }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  fetchBankName(ifsc: string): Observable<BankNameResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/accounting/bank-accounts/v1/bank-name/?ifsc=${ifsc}`;
    let httpOptions = {
      headers: new HttpHeaders({ Authorization: token }),
    };
    return this.http.get<BankNameResponse>(url, httpOptions);
  }

  updateBankAccount(body: UpdateBankAccountBody): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/accounting/staff/v1/bank-account/`;
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
        "Content-Type": "application/json",
      }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  updatePanNumber(body: {
    user_id: number;
    pan: string;
  }): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/pan/`;
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
        "Content-Type": "application/json",
      }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  addInvestor(body: {
    name: string;
    email: string;
    mobile_number: string;
    pan: string;
    dob: string;
    gender: string;
  }): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem('authToken')}`;
    const url = `${environment.baseUrl}apis/users/partners/v1/add-investor/`;
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
        'Content-Type': 'application/json',
      }),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  updateShowBonds(user_id: number, show_bonds: boolean): Observable<IGenericResponse> {
    const token = `Token ${localStorage.getItem("authToken")}`;
    const url = `${environment.baseUrl}apis/users/staff/v1/show-bonds/`;
    let httpOptions = {
      headers: new HttpHeaders({
        Authorization: token,
        "Content-Type": "application/json",
      }),
    };
    const body = {
      user_id: user_id,
      show_bonds: show_bonds.toString(),
    };
    return this.http.post<IGenericResponse>(url, body, httpOptions);
  }

  fetchGoldHoldings(
    filters: Record<string, string | number | null | undefined>
  ): Observable<IGoldHoldingListResponse> {
    return this.http.get<IGoldHoldingListResponse>(
      `${environment.baseUrl}apis/investor/staff/gold/v1/holdings/`,
      this.staffListOptions(filters)
    );
  }

  fetchGoldTransactions(
    filters: Record<string, string | number | null | undefined>
  ): Observable<IGoldListResponse<GoldTransaction>> {
    return this.http.get<IGoldListResponse<GoldTransaction>>(
      `${environment.baseUrl}apis/investor/staff/gold/v1/transactions/`,
      this.staffListOptions(filters)
    );
  }

  fetchGoldFDs(
    filters: Record<string, string | number | null | undefined>
  ): Observable<IGoldListResponse<GoldFD>> {
    return this.http.get<IGoldListResponse<GoldFD>>(
      `${environment.baseUrl}apis/investor/staff/gold/v1/fds/`,
      this.staffListOptions(filters)
    );
  }

  private staffListOptions(
    filters: Record<string, string | number | null | undefined>
  ) {
    const token = `Token ${localStorage.getItem("authToken")}`;
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        params = params.set(key, String(value));
      }
    });
    return {
      headers: new HttpHeaders({ Authorization: token }),
      params,
    };
  }
}
